import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const STEPS = [
  {
    n: "01",
    icon: "fa-magnifying-glass",
    color: "brand-red",
    title: "Browse & Book",
    body:
      "Pick your event dates, choose your gear (tents, chairs, tables, linens, chafing sets), add any customizations. See live availability at the pod nearest you.",
  },
  {
    n: "02",
    icon: "fa-credit-card",
    color: "brand-gold",
    title: "Pay Online",
    body:
      "Secure checkout via Stripe. Your card is charged for the rental total; a $50 refundable deposit is authorized separately (auth-hold — not captured unless something breaks).",
  },
  {
    n: "03",
    icon: "fa-key",
    color: "brand-blue",
    title: "Receive Your PIN",
    body:
      "A 6-digit PIN is sent by SMS and email the moment your rental window starts. It only works during your booked window — nobody else can enter.",
  },
  {
    n: "04",
    icon: "fa-bolt",
    color: "brand-red",
    title: "Unlock the Pod",
    body:
      "Walk up to the keybox, enter your PIN, grab your gear. No staff, no waiting, no small-talk. 24/7 self-serve, in and out in minutes.",
  },
  {
    n: "05",
    icon: "fa-champagne-glasses",
    color: "brand-gold",
    title: "Celebrate",
    body:
      "Have a great event. Take pictures. Enjoy — this is what the gear is for.",
  },
  {
    n: "06",
    icon: "fa-arrow-rotate-left",
    color: "brand-blue",
    title: "Return & Lock Up",
    body:
      "Return items to the pod, snap a photo of the closed lock, and we auto-release your deposit. Late? A small late fee (1× day-rate) accrues per day — that's it.",
  },
];

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-white" data-testid="how-it-works-page">
      <Navbar />

      {/* HERO */}
      <section className="bg-brand-blue text-white py-16 md:py-24 border-b-8 border-brand-red overflow-hidden relative">
        <div className="absolute -right-20 -bottom-20 opacity-10 pointer-events-none">
          <i className="fas fa-bolt text-[300px] text-brand-gold" />
        </div>
        <div className="max-w-6xl mx-auto px-4 relative">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-gold mb-3">How It Works</p>
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tightest leading-none">
            Rent. Unlock. <span className="text-brand-gold italic">Celebrate.</span>
          </h1>
          <p className="text-slate-200 text-sm md:text-base font-bold mt-5 max-w-2xl leading-relaxed">
            Party Rentals Express is the first fully self-serve rental pod in Denver. No warehouse visits, no delivery windows, no staff. Just gear, a PIN, and a keybox.
          </p>
        </div>
      </section>

      {/* STEPS */}
      <section className="max-w-6xl mx-auto px-4 py-16 space-y-6">
        {STEPS.map((step, idx) => (
          <div
            key={step.n}
            className={`grid md:grid-cols-[120px_1fr] gap-6 items-start bg-white rounded-[28px] border border-slate-100 p-6 md:p-8 ${idx % 2 === 0 ? "" : "md:flex-row-reverse"}`}
            data-testid={`how-step-${step.n}`}
          >
            <div className={`flex md:flex-col items-center md:items-start gap-3`}>
              <p className={`text-5xl md:text-6xl font-black tracking-tightest text-${step.color}`}>{step.n}</p>
              <div className={`w-14 h-14 rounded-2xl bg-${step.color}/10 text-${step.color} flex items-center justify-center`}>
                <i className={`fas ${step.icon} text-2xl`} />
              </div>
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tightest text-brand-blue mb-3">{step.title}</h3>
              <p className="text-slate-600 text-sm md:text-base font-bold leading-relaxed">{step.body}</p>
            </div>
          </div>
        ))}
      </section>

      {/* FAQ / SAFETY */}
      <section className="bg-brand-slate py-16 border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-4 space-y-6">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red">The Fine Print</p>
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tightest text-brand-blue">A few things to know.</h2>
          </div>
          <FaqRow q="Is my deposit really refundable?"
            a="Yes. We use Stripe's manual-capture flow — the $50 is authorized on your card but not actually charged. When you lock the pod and mark your rental complete, we release the hold automatically." />
          <FaqRow q="What if I want to add gear mid-rental?"
            a="Log in, hit your booking, and add on more items. Same PIN, same window — the extra fee just charges to the same card." />
          <FaqRow q="Is the pod really unstaffed?"
            a="Yes. Cameras watch the pod 24/7 for safety, but there's no attendant. If you get stuck, hit the on-site help button or text our support line." />
          <FaqRow q="What if I break something?"
            a="Report it in the app. Small damages come out of your deposit; larger claims we handle case-by-case. Fair is our policy." />
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto text-center py-16 px-4">
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-3">Ready?</p>
        <h3 className="text-3xl md:text-4xl font-black uppercase tracking-tightest text-brand-blue mb-6">Book your first rental.</h3>
        <Link
          to="/locations"
          className="inline-flex items-center gap-2 bg-brand-red text-white px-8 py-4 rounded-full text-sm font-black uppercase tracking-widest hover:bg-red-700 transition-colors"
          data-testid="how-it-works-cta"
        >
          <i className="fas fa-map-pin" /> Find A Pod Near Me
          <i className="fas fa-arrow-right" />
        </Link>
      </section>

      <Footer />
    </div>
  );
}

function FaqRow({ q, a }) {
  return (
    <details className="bg-white rounded-2xl border border-slate-100 p-5 group" data-testid="how-it-works-faq-row">
      <summary className="cursor-pointer flex items-center justify-between font-black uppercase text-xs tracking-tight text-brand-blue">
        {q}
        <i className="fas fa-plus text-brand-red group-open:rotate-45 transition-transform" />
      </summary>
      <p className="text-slate-600 text-sm font-bold mt-3 leading-relaxed">{a}</p>
    </details>
  );
}
